import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { DeleteOutline as DeleteOutlineIcon } from "@mui/icons-material";

const SERVICING_TIME_OPTIONS = [
  { value: 15, text: "15 minutes" },
  { value: 30, text: "30 minutes" },
  { value: 45, text: "45 minutes" },
  { value: 60, text: "1 hour" },
  { value: 75, text: "1 hour 15 minutes" },
  { value: 90, text: "1 hour 30 minutes" },
  { value: 105, text: "1 hour 45 minutes" },
  { value: 120, text: "2 hours" },
  { value: 135, text: "2 hours 15 minutes" },
  { value: 150, text: "2 hours 30 minutes" },
  { value: 165, text: "2 hours 45 minutes" },
  { value: 180, text: "3 hours" },
  { value: 195, text: "3 hours 15 minutes" },
  { value: 210, text: "3 hours 30 minutes" },
  { value: 225, text: "3 hours 45 minutes" },
  { value: 240, text: "4 hours" },
  { value: 255, text: "4 hours 15 minutes" },
  { value: 270, text: "4 hours 30 minutes" },
  { value: 285, text: "4 hours 45 minutes" },
  { value: 300, text: "5 hours" },
  { value: 315, text: "5 hours 15 minutes" },
  { value: 330, text: "5 hours 30 minutes" },
  { value: 345, text: "5 hours 45 minutes" },
  { value: 360, text: "6 hours" },
  { value: 375, text: "6 hours 15 minutes" },
  { value: 390, text: "6 hours 30 minutes" },
  { value: 405, text: "6 hours 45 minutes" },
  { value: 420, text: "7 hours" },
  { value: 435, text: "7 hours 15 minutes" },
  { value: 450, text: "7 hours 30 minutes" },
  { value: 465, text: "7 hours 45 minutes" },
  { value: 480, text: "8 hours" },
];

function isFreeService(service) {
  return (
    !service.service_price_level_1 &&
    !service.service_price_level_2 &&
    !service.service_price_level_3 &&
    !service.service_price_level_4 &&
    !service.service_price_level_5
  );
}

export function UserServiceForm({
  service,
  formData,
  loading,
  deleteService,
  hasError,
  onFieldChange,
}) {
  const serviceId = service.service_id;
  const current = formData.current[serviceId] || {};
  const free = isFreeService(service);

  const [, forceRender] = useState(0);
  const bump = () => forceRender((n) => n + 1);

  const priceOptions = useMemo(
    () => [
      {
        value: 1,
        text: `Price Level 1: $${service.service_price_level_1 ?? 0}`,
      },
      {
        value: 2,
        text: `Price Level 2: $${service.service_price_level_2 ?? 0}`,
      },
      {
        value: 3,
        text: `Price Level 3: $${service.service_price_level_3 ?? 0}`,
      },
      {
        value: 4,
        text: `Price Level 4: $${service.service_price_level_4 ?? 0}`,
      },
      {
        value: 5,
        text: `Price Level 5: $${service.service_price_level_5 ?? 0}`,
      },
    ],
    [service]
  );

  const updateField = (field, value) => {
    formData.current[serviceId] = {
      ...formData.current[serviceId],
      [field]: value,
    };
    onFieldChange?.(serviceId);
    bump();
  };

  return (
    <Box
      sx={{
        mb: 2.5,
        p: 2.5,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: hasError ? "error.main" : "divider",
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={700} sx={{ fontSize: "1.1rem" }}>
            {service.service_name}
          </Typography>
          {service.service_shortcut ? (
            <Typography variant="body2" color="text.secondary">
              Shortcut: {service.service_shortcut}
            </Typography>
          ) : null}
        </Box>
        {typeof deleteService === "function" ? (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
            onClick={() => deleteService(serviceId)}
            disabled={loading}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Remove
          </Button>
        ) : null}
      </Box>

      <Stack spacing={2}>
        {free ? (
          <Typography variant="body2" color="text.secondary">
            Free service — price level is not required.
          </Typography>
        ) : (
          <FormControl fullWidth size="small" color="primary" disabled={loading}>
            <InputLabel id={`price-level-${serviceId}`}>Price level</InputLabel>
            <Select
              labelId={`price-level-${serviceId}`}
              label="Price level"
              value={current.priceLevel ?? ""}
              onChange={(e) => updateField("priceLevel", Number(e.target.value))}
            >
              {priceOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          <FormControl fullWidth size="small" color="primary" disabled={loading}>
            <InputLabel id={`servicing-${serviceId}`}>Servicing</InputLabel>
            <Select
              labelId={`servicing-${serviceId}`}
              label="Servicing"
              value={current.servicing ?? ""}
              onChange={(e) => updateField("servicing", Number(e.target.value))}
            >
              {SERVICING_TIME_OPTIONS.map((opt) => (
                <MenuItem key={`s-${opt.value}`} value={opt.value}>
                  {opt.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" color="primary" disabled={loading}>
            <InputLabel id={`gap-${serviceId}`}>Gap</InputLabel>
            <Select
              labelId={`gap-${serviceId}`}
              label="Gap"
              value={current.gap ?? ""}
              onChange={(e) => updateField("gap", Number(e.target.value))}
            >
              {SERVICING_TIME_OPTIONS.map((opt) => (
                <MenuItem key={`g-${opt.value}`} value={opt.value}>
                  {opt.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" color="primary" disabled={loading}>
            <InputLabel id={`servicing2-${serviceId}`}>
              Servicing after gap
            </InputLabel>
            <Select
              labelId={`servicing2-${serviceId}`}
              label="Servicing after gap"
              value={current.servicing2 ?? ""}
              onChange={(e) =>
                updateField("servicing2", Number(e.target.value))
              }
            >
              {SERVICING_TIME_OPTIONS.map((opt) => (
                <MenuItem key={`s2-${opt.value}`} value={opt.value}>
                  {opt.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>
    </Box>
  );
}
